import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Paper, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Chip, Divider, Button, Stack, Avatar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShareIcon from '@mui/icons-material/Share';
import HandshakeIcon from '@mui/icons-material/Handshake';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axiosInstance from '../utils/axiosConfig';

// ── Types ──────────────────────────────────────────────────────────

interface StatMetric {
  window: number;
  total: number;
  today?: number;
}

interface DailyBucket {
  _id: { year: number; month: number; day: number };
  count: number;
}

interface BandRow {
  bandId: string;
  bandName: string;
  mainImage?: string | null;
  views: number;
  shares: number;
  interests: number;
  qrScans: number;
  total: number;
}

interface TopMusician {
  _id: string;
  views: number;
}

interface RecentInterest {
  bandName: string;
  timestamp: string;
}

interface UtmEntry {
  _id: string;
  count: number;
}

interface Summary {
  pageViews: StatMetric;
  shares: StatMetric;
  interests: StatMetric;
  qrGenerated: StatMetric;
  qrScans: StatMetric;
  audioPlays: StatMetric;
  musicianViews: StatMetric;
  emailsSent: StatMetric;
  topBandsTable: BandRow[];
  sharesByChannel: Record<string, number>;
  dailyViewsWindow: DailyBucket[];
  dailySharesWindow: DailyBucket[];
  dailyViews90d: DailyBucket[];
  topMusicians: TopMusician[];
  recentInterests: RecentInterest[];
  utmSourceBreakdown: UtmEntry[];
  window: { from: string; to: string };
}

// ── Colours ────────────────────────────────────────────────────────

const CHANNEL_COLOURS: Record<string, string> = {
  native_share: '#50E3C2',
  whatsapp:     '#25D366',
  linkedin:     '#0A66C2',
  email:        '#F59E0B',
  copy:         '#8B5CF6',
  unknown:      '#94A3B8',
};

const GRID_STROKE  = 'rgba(255,255,255,0.08)';
const TICK_COLOUR  = 'rgba(255,255,255,0.5)';

// ── Dark tooltip ───────────────────────────────────────────────────

const DarkTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: 'rgba(10,10,20,0.96)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 1.5,
      px: 1.75, py: 1.25,
      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    }}>
      {label && (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      {payload.map((p: any) => (
        <Typography key={p.dataKey} variant="body2" sx={{ color: p.color || '#fff', fontWeight: 600, lineHeight: 1.6 }}>
          {p.name}: {p.value?.toLocaleString()}
        </Typography>
      ))}
    </Box>
  );
};

// ── Helpers ────────────────────────────────────────────────────────

function bucketToDate(b: DailyBucket['_id']): string {
  return `${b.day}/${b.month}`;
}

function flattenDaily(buckets: DailyBucket[]): { date: string; count: number }[] {
  return buckets.map(b => ({ date: bucketToDate(b._id), count: b.count }));
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Stat Card ──────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  icon: React.ReactNode;
  color: string;
  windowCount: number;
  total: number;
  today?: number;
  windowLabel: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, icon, color, windowCount, total, today, windowLabel }) => (
  <Paper elevation={2} sx={{
    p: 2.5, borderRadius: 3, borderTop: `4px solid ${color}`,
    height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle2" color="text.secondary" fontWeight={600}
        sx={{ letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
        {label}
      </Typography>
    </Box>
    <Typography variant="h3" fontWeight={800} sx={{ color, lineHeight: 1 }}>
      {windowCount.toLocaleString()}
    </Typography>
    <Typography variant="caption" color="text.secondary">{windowLabel}</Typography>
    <Divider />
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>{total.toLocaleString()}</Typography>
        <Typography variant="caption" color="text.secondary">All time</Typography>
      </Box>
      {today !== undefined && (
        <Box>
          <Typography variant="h6" fontWeight={700}>{today.toLocaleString()}</Typography>
          <Typography variant="caption" color="text.secondary">Today</Typography>
        </Box>
      )}
    </Box>
  </Paper>
);

// ── Presets ────────────────────────────────────────────────────────

const makePresets = () => {
  const now = new Date();
  const startToday = new Date(now); startToday.setHours(0, 0, 0, 0);
  const ago7  = new Date(now); ago7.setDate(now.getDate() - 7);
  const ago30 = new Date(now); ago30.setDate(now.getDate() - 30);
  const ago90 = new Date(now); ago90.setDate(now.getDate() - 90);
  return [
    { label: 'Today',   from: startToday, to: now },
    { label: '7 days',  from: ago7,       to: now },
    { label: '30 days', from: ago30,      to: now },
    { label: '90 days', from: ago90,      to: now },
  ];
};

// ── Main Page ──────────────────────────────────────────────────────

type SortField = 'views' | 'shares' | 'interests' | 'qrScans' | 'total';

const AdminAnalyticsPage: React.FC = () => {
  const presets = makePresets();
  const defaultFrom = presets[2].from; // 30 days
  const defaultTo   = presets[2].to;

  const [summary, setSummary]   = useState<Summary | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('total');
  const [sortDir, setSortDir]   = useState<'asc' | 'desc'>('desc');
  const [dateFrom, setDateFrom] = useState<Date>(defaultFrom);
  const [dateTo, setDateTo]     = useState<Date>(defaultTo);

  const load = useCallback(async (from: Date, to: Date) => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/api/analytics/summary', {
        params: { from: from.toISOString(), to: to.toISOString() },
      });
      setSummary(data);
      setError(null);
    } catch {
      setError('Failed to load analytics. Check that you are logged in as admin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(dateFrom, dateTo); }, [load, dateFrom, dateTo]);

  const applyPreset = (from: Date, to: Date) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  // ── CSV export ─────────────────────────────────────────────────

  const exportCSV = () => {
    if (!summary) return;
    const periodLabel = `${fmtDate(dateFrom)} to ${fmtDate(dateTo)}`;
    const rows = [
      [`This Is Irish Jazz — Analytics Export`],
      [`Period: ${periodLabel}`],
      [`Generated: ${fmtDate(new Date())}`],
      [],
      ['Metric', 'Selected Period', 'All Time', 'Today'],
      ['Page Views',        summary.pageViews.window,     summary.pageViews.total,     summary.pageViews.today ?? ''],
      ['Shares',            summary.shares.window,        summary.shares.total,        summary.shares.today ?? ''],
      ['Interests Expressed', summary.interests.window,   summary.interests.total,     summary.interests.today ?? ''],
      ['QR Codes Generated', summary.qrGenerated.window,  summary.qrGenerated.total,   ''],
      ['QR Scans',          summary.qrScans.window,       summary.qrScans.total,       ''],
      ['Audio Plays',       summary.audioPlays.window,    summary.audioPlays.total,    ''],
      ['Musician Pages',    summary.musicianViews.window, summary.musicianViews.total, ''],
      ['Profile Emails',    summary.emailsSent.window,    summary.emailsSent.total,    ''],
      [],
      ['Band', 'Views', 'Shares', 'Interests', 'QR Scans', 'Total'],
      ...sortedBands(summary).map(b => [
        b.bandName || '—', b.views, b.shares, b.interests, b.qrScans, b.total,
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `imc-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ── PDF export ─────────────────────────────────────────────────

  const exportPDF = () => {
    if (!summary) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const periodLabel = `${fmtDate(dateFrom)} – ${fmtDate(dateTo)}`;

    // Header
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text('This Is Irish Jazz', 14, 18);
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text('Audience & Engagement Report', 14, 25);
    doc.text(`Period: ${periodLabel}`, 14, 31);
    doc.text(`Generated: ${fmtDate(new Date())}`, 14, 37);

    // Summary table
    doc.setFontSize(13);
    doc.setTextColor(40, 40, 40);
    doc.text('Summary', 14, 48);

    autoTable(doc, {
      startY: 52,
      head: [['Metric', 'Selected Period', 'All Time', 'Today']],
      body: [
        ['Page Views',          summary.pageViews.window,     summary.pageViews.total,     summary.pageViews.today ?? '—'],
        ['Shares',              summary.shares.window,        summary.shares.total,        summary.shares.today ?? '—'],
        ['Interests Expressed', summary.interests.window,     summary.interests.total,     summary.interests.today ?? '—'],
        ['QR Codes Generated',  summary.qrGenerated.window,   summary.qrGenerated.total,   '—'],
        ['QR Scans',            summary.qrScans.window,       summary.qrScans.total,       '—'],
        ['Audio Plays',         summary.audioPlays.window,    summary.audioPlays.total,    '—'],
        ['Musician Pages',      summary.musicianViews.window, summary.musicianViews.total, '—'],
        ['Profile Emails Sent', summary.emailsSent.window,    summary.emailsSent.total,    '—'],
      ],
      styles: { fontSize: 10 },
      headStyles: { fillColor: [30, 80, 120], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    // Band table
    const finalY = (doc as any).lastAutoTable.finalY || 140;
    doc.setFontSize(13);
    doc.text('Top Bands — Selected Period', 14, finalY + 12);

    autoTable(doc, {
      startY: finalY + 16,
      head: [['#', 'Band', 'Views', 'Shares', 'Interests', 'QR Scans', 'Total']],
      body: sortedBands(summary).map((b, i) => [
        i + 1, b.bandName || '—', b.views, b.shares, b.interests, b.qrScans, b.total,
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 80, 120], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: { 0: { cellWidth: 10 } },
    });

    // Share breakdown
    if (Object.keys(summary.sharesByChannel).length > 0) {
      const y2 = (doc as any).lastAutoTable.finalY || 200;
      if (y2 + 40 > 280) doc.addPage();
      const startY = y2 + 40 > 280 ? 20 : y2 + 12;
      doc.setFontSize(13);
      doc.text('Shares by Channel', 14, startY);
      autoTable(doc, {
        startY: startY + 4,
        head: [['Channel', 'Count']],
        body: Object.entries(summary.sharesByChannel).map(([ch, n]) => [ch, n]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [30, 80, 120], textColor: 255, fontStyle: 'bold' },
      });
    }

    doc.save(`imc-analytics-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // ── Helpers used by both export and render ─────────────────────

  function sortedBands(s: Summary): BandRow[] {
    return [...s.topBandsTable].sort((a, b) => {
      const diff = a[sortField] - b[sortField];
      return sortDir === 'asc' ? diff : -diff;
    });
  }

  // ── Loading / error ────────────────────────────────────────────

  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Loading analytics…</Typography>
      </Box>
    );
  }

  if (error || !summary) {
    return <Alert severity="error" sx={{ mt: 4 }}>{error || 'No data'}</Alert>;
  }

  const {
    pageViews, shares, interests, qrGenerated, qrScans,
    audioPlays, musicianViews, emailsSent,
    sharesByChannel,
    dailyViewsWindow, dailySharesWindow,
    topMusicians, recentInterests, utmSourceBreakdown,
  } = summary;

  const bands = sortedBands(summary);

  // Combine daily views + shares into one dataset
  const viewMap: Record<string, number> = {};
  flattenDaily(dailyViewsWindow).forEach(d => { viewMap[d.date] = d.count; });
  const shareMap: Record<string, number> = {};
  flattenDaily(dailySharesWindow).forEach(d => { shareMap[d.date] = d.count; });
  const allDates = Array.from(new Set([...Object.keys(viewMap), ...Object.keys(shareMap)]));
  const combinedDaily = allDates.map(date => ({
    date,
    views:  viewMap[date]  || 0,
    shares: shareMap[date] || 0,
  }));

  const channelPieData = Object.entries(sharesByChannel)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const windowLabel = `${fmtDate(dateFrom)} – ${fmtDate(dateTo)}`;
  const axisProps = { tick: { fill: TICK_COLOUR, fontSize: 11 } };

  // ── Render ─────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Analytics</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            This Is Irish Jazz — Audience &amp; Engagement Data
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportCSV}>
            CSV
          </Button>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={exportPDF}>
            PDF
          </Button>
        </Stack>
      </Box>

      {/* ── Date range controls ─────────────────────────────────── */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {makePresets().map(p => {
              const active = fmtDate(dateFrom) === fmtDate(p.from) && fmtDate(dateTo) === fmtDate(p.to);
              return (
                <Chip
                  key={p.label}
                  label={p.label}
                  size="small"
                  variant={active ? 'filled' : 'outlined'}
                  color={active ? 'primary' : 'default'}
                  onClick={() => applyPreset(p.from, p.to)}
                  sx={{ cursor: 'pointer' }}
                />
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <DatePicker
              label="From"
              value={dateFrom}
              onChange={(d) => d && setDateFrom(d)}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
            <DatePicker
              label="To"
              value={dateTo}
              onChange={(d) => d && setDateTo(d)}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
          </Box>
        </Box>
      </Paper>

      {/* ── 8 Stat Cards ───────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {[
          { label: 'Page Views',         icon: <TrendingUpIcon />,      color: '#3B82F6', metric: pageViews },
          { label: 'Shares',             icon: <ShareIcon />,           color: '#50E3C2', metric: shares },
          { label: 'Interests Expressed',icon: <HandshakeIcon />,       color: '#F59E0B', metric: interests },
          { label: 'QR Codes Generated', icon: <QrCode2Icon />,         color: '#8B5CF6', metric: qrGenerated },
          { label: 'QR Scans',           icon: <QrCodeScannerIcon />,   color: '#EC4899', metric: qrScans },
          { label: 'Audio Plays',        icon: <MusicNoteIcon />,       color: '#10B981', metric: audioPlays },
          { label: 'Musician Pages',     icon: <PersonIcon />,          color: '#F97316', metric: musicianViews },
          { label: 'Profile Emails Sent',icon: <EmailIcon />,           color: '#6366F1', metric: emailsSent },
        ].map(({ label, icon, color, metric }) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <StatCard
              label={label} icon={icon} color={color}
              windowCount={metric.window}
              total={metric.total}
              today={metric.today}
              windowLabel={windowLabel}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Charts ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>

        {/* Daily activity line chart */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Daily Activity — {windowLabel}
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={combinedDaily} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="date" {...axisProps} />
                <YAxis {...axisProps} />
                <RechartsTip content={<DarkTooltip />} />
                <Legend wrapperStyle={{ color: TICK_COLOUR }} />
                <Line type="monotone" dataKey="views" stroke="#3B82F6" strokeWidth={2} dot={false} name="Page Views" />
                <Line type="monotone" dataKey="shares" stroke="#50E3C2" strokeWidth={2} dot={false} name="Shares" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Share channel pie */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Shares by Channel
            </Typography>
            {channelPieData.length === 0 ? (
              <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>No share data yet</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={channelPieData} cx="50%" cy="50%"
                    innerRadius={60} outerRadius={100} paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {channelPieData.map((entry, i) => (
                      <Cell key={i} fill={CHANNEL_COLOURS[entry.name] || '#94A3B8'} />
                    ))}
                  </Pie>
                  <RechartsTip content={<DarkTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* UTM source bar */}
        {utmSourceBreakdown.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Inbound Traffic Sources (UTM)
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  layout="vertical"
                  data={utmSourceBreakdown.map(u => ({ name: u._id, count: u.count }))}
                  margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                  <XAxis type="number" {...axisProps} />
                  <YAxis type="category" dataKey="name" {...axisProps} />
                  <RechartsTip content={<DarkTooltip />} />
                  <Bar dataKey="count" fill="#8B5CF6" name="Sessions" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

      </Grid>

      {/* ── Tables ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5}>

        {/* Top bands sortable table */}
        <Grid item xs={12} lg={8}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Top Bands — {windowLabel}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Band</strong></TableCell>
                    {(['views', 'shares', 'interests', 'qrScans', 'total'] as SortField[]).map(f => (
                      <TableCell key={f} align="right">
                        <TableSortLabel
                          active={sortField === f}
                          direction={sortField === f ? sortDir : 'desc'}
                          onClick={() => handleSort(f)}
                        >
                          <strong>{f === 'qrScans' ? 'QR' : f.charAt(0).toUpperCase() + f.slice(1)}</strong>
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bands.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                        No data for this period — data appears as users visit band pages
                      </TableCell>
                    </TableRow>
                  ) : (
                    bands.map((row, i) => (
                      <TableRow key={row.bandId} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Avatar
                              src={row.mainImage || undefined}
                              alt={row.bandName}
                              sx={{ width: 36, height: 36, flexShrink: 0, fontSize: '0.85rem' }}
                            >
                              {(row.bandName || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Chip label={i + 1} size="small" sx={{ minWidth: 24, height: 18, fontSize: '0.65rem', mr: 0.75 }} />
                              <Typography variant="body2" fontWeight={600} component="span">{row.bandName || '—'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{row.views}</TableCell>
                        <TableCell align="right">{row.shares}</TableCell>
                        <TableCell align="right">{row.interests}</TableCell>
                        <TableCell align="right">{row.qrScans}</TableCell>
                        <TableCell align="right"><strong>{row.total}</strong></TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Top musician pages */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Top Musician Pages
              </Typography>
              {topMusicians.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No musician page views yet</Typography>
              ) : (
                topMusicians.map((m, i) => (
                  <Box key={m._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: '#F97316' }}>
                        {m._id.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="body2">{m._id}</Typography>
                    </Box>
                    <Chip label={m.views} size="small" color="primary" variant="outlined" />
                  </Box>
                ))
              )}
            </Paper>

            {/* Recent interests */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Recent Interest Expressed
              </Typography>
              {recentInterests.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No interests yet</Typography>
              ) : (
                recentInterests.map((r, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
                    <Typography variant="body2" fontWeight={r.bandName ? 600 : 400}>
                      {r.bandName || 'General'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(r.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </Typography>
                  </Box>
                ))
              )}
            </Paper>

          </Box>
        </Grid>

      </Grid>
    </Box>
  );
};

export default AdminAnalyticsPage;
